/*    
<summary>
  This functional component "AddStoreManagement" defines the props like initial values and functions for the child form component.
  Developer:Aashish Singh, Created Date:29-August-2023
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import toast from "react-hot-toast";
import { cloneDeep } from "lodash";
import { useStore } from "../../../../contexts/store-provider";
import {
  IAddStoreManagement,
  IUpdateStoreManagement,
} from "../../../../models/forms/IAddStore";
import { formatMessage } from "../../../../translations/format-message";
import AddStoreManagementForm from "../forms/add-edit-store-form";
import { businessTypeOptions } from "../../../../constants/options.constant";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";

interface IProps {
  id: number;
  modalClosed: () => void;
}

const AddStoreManagement = (props: IProps) => {
  const { storeManagementStore, masterManagementStore } = useStore();
  const {
    GetStoreManagementByIdService,
    resetAddUpdateStoreManagementState,
    resetStoreManagementDetail,
    storeState,
    storeDetail,
    addUpdateStoreManagementState,
    AddStoreManagementService,
    UpdateStoreManagementService,
    inProgress,
    DeleteStoreManagementService,
    deleteStoreManagementState,
    GetPrefectureUsingPostalCode,
    prefectureByZipCode,
    resetPrefectureByZipCode,
  } = storeManagementStore;
  const { getAllBusinessTypes, GetAllBusinessTypeService } =
    masterManagementStore;
  const initialValues: any = storeDetail;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllBusinessTypeService();
    if (props.id > 0) GetStoreManagementByIdService(props.id);
    if (!(props.id > 0)) resetStoreManagementDetail();
  }, []);

  // useEffect(() => {
  //   if ((props.id > 0) && (initialValues?.Id)){
  //     GetPrefectureUsingPostalCode(initialValues?.PostalCode);
  //   }
  // }, [initialValues]);

  // useEffect(()=> {
  //   if(prefectureByZipCode !== null){

  //   }
  // },[prefectureByZipCode])

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreManagementState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateStoreManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (deleteStoreManagementState.success) {
      toast.success(formatMessage("deleted_success"));
      onDeleteModalClosed();
    }
  }, [deleteStoreManagementState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "storeDetailError" of the useEffect changed.
   */
  useEffect(() => {
    if (storeState.error) {
      toast.error(formatMessage(storeState.error));
      resetStoreManagementDetail();
    }
  }, [storeState.error]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateStoreManagementError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateStoreManagementState.error) {
      toast.error(formatMessage(addUpdateStoreManagementState.error));
      resetAddUpdateStoreManagementState();
    }
  }, [addUpdateStoreManagementState.error]);

  /**
   * This function adds the store when "Id < 0" and updates the Existing store when Id > 0 by providing form values to the
   * correponding function
   */
  const onSubmitForm = (
    values: IAddStoreManagement | IUpdateStoreManagement
  ) => {
    let tempValues = cloneDeep(values);
    tempValues.StoreName = values.StoreName.trim();
    if (props.id < 0) AddStoreManagementService(tempValues);
    else
      UpdateStoreManagementService({
        ...tempValues,
        Id: props.id,
        IsPasswordUpdated: false,
      });
  };

  const handleDeleteSubmitHandler = () => {
    DeleteStoreManagementService(props.id);
  };

  /**
   * This function closes the pop up modal and reset getstoredetails, addUpdate store observables (success, inprogress
   * and error).
   */
  const onDeleteModalClosed = () => {
    resetStoreManagementDetail();
    props.modalClosed();
  };

  const onModalClosed = () => {
    resetStoreManagementDetail();
    resetPrefectureByZipCode();
    props.modalClosed();
  };

  return (
    <React.Fragment>
      <AddStoreManagementForm
        initialValues={initialValues}
        id={props.id}
        businessTypeOptions={getAllBusinessTypes}
        submitHandler={onSubmitForm}
        onModalClosed={onModalClosed}
        handleDeleteSubmitHandler={handleDeleteSubmitHandler}
        onDeleteModalClosed={onDeleteModalClosed}
        getStoreManagementLoading={storeState.inProgress || inProgress}
        viewOnly={
          props.id > 0
            ? !isOperationPermittedRoleBase(
                ModuleType.StoreManagement,
                ActionType.Add
              )
            : false
        }
        addUpdateStoreManagementLoading={
          addUpdateStoreManagementState.inProgress
        }
      />
    </React.Fragment>
  );
};

export default observer(AddStoreManagement);
